import { useState, useEffect } from 'react';
import { BrowserRouter as Router, useLocation } from 'react-router-dom';
import { DashboardAdmin } from './pages/DashboardAdmin';
import { DashboardClient } from './pages/DashboardClient';
import { Organizations } from './pages/Organizations';
import { Learners } from './pages/Learners';
import { Programs } from './pages/Programs';
import { Groups } from './pages/Groups';
import { Settings } from './pages/Settings';
import { Landing } from './pages/Landing';
import { ResetPassword } from './pages/ResetPassword';
import { useAuth } from './contexts/AuthContext';
import { Sidebar } from './components/Layout/Sidebar';
import { LoadingSpinner } from './components/UI/LoadingSpinner';
import { SidebarProvider } from './contexts/SidebarContext';
import { NotFound } from './pages/NotFound';
import { LMS_URL } from './types';

type PageId = 'dashboard' | 'organizations' | 'learners' | 'groups' | 'programs' | 'settings';

function AppContent() {
  const [currentPage, setCurrentPage] = useState<PageId>('dashboard');
  const { user, profile, loading, logout, isSuperAdmin, isAllowedInManager } = useAuth();
  const location = useLocation();

  // Redirection des utilisateurs non autorises (learners) vers le LMS
  useEffect(() => {
    if (!loading && user && profile && !isAllowedInManager) {
      console.log('[Auth] Role non autorise dans le Manager:', profile.role);
      console.log('[Auth] Redirection vers le LMS:', LMS_URL);
      window.location.href = LMS_URL;
    }
  }, [loading, user, profile, isAllowedInManager]);

  // Page de réinitialisation de mot de passe (accessible sans connexion)
  if (location.pathname === '/reset-password') {
    return <ResetPassword />;
  }

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Landing />;
  }

  // Afficher un loader pendant la redirection des utilisateurs non autorises
  if (!isAllowedInManager) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner />
          <p className="mt-4 text-ink-soft">
            Redirection vers l'espace apprenant...
          </p>
        </div>
      </div>
    );
  }

  const renderPage = () => {
    switch (currentPage) {
      case 'dashboard':
        return isSuperAdmin ? <DashboardAdmin /> : <DashboardClient />;
      case 'organizations':
        // Only super admin can access
        return isSuperAdmin ? <Organizations /> : <DashboardClient />;
      case 'learners':
        return <Learners />;
      case 'groups':
        return <Groups />;
      case 'programs':
        return <Programs />;
      case 'settings':
        // Only super admin can access
        return isSuperAdmin ? <Settings /> : <DashboardClient />;
      default:
        return <NotFound />;
    }
  };

  return (
    <div className="min-h-screen bg-cream text-ink">
      <Sidebar
        currentPage={currentPage}
        onPageChange={(page) => setCurrentPage(page as PageId)}
        onLogout={logout}
      />
      <main className="lg:ml-64 p-6">
        <div className="max-w-7xl mx-auto">
          {renderPage()}
        </div>
      </main>
    </div>
  );
}

function App() {
  return (
    <Router>
      <SidebarProvider>
        <AppContent />
      </SidebarProvider>
    </Router>
  );
}

export default App;
